import * as Yup from 'yup';

import { TimeoutAdornment } from '@components';
import { TIMEOUT_LIMITS } from '@constants';
import {
  ApiEndpoint,
  columnDirection,
  FormActionType,
  FormType,
  InputType,
  originType,
  ServiceModalName,
  Translation,
} from '@enums';
import i18n from '@translations';
import type { EndpointType, FormConfig, FormConfigMapping } from '@types';
import { optionType } from '@utils';

interface GetDomainFormConfigParams {
  endpoint?: EndpointType;
  isEdit?: boolean;
  originOptionList?: optionType[];
  schema: Yup.AnySchema;
}

export const getDomainFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  isEdit = false,
  originOptionList = [],
  schema,
}: GetDomainFormConfigParams): FormConfig => {
  const tModal: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:modals.domain.${key}`);

  const tButton: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:buttons.domain.${key}`);

  const tForm: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:forms.domain.${key}`);

  const tValidation: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Validation}:format.${key}`);

  const tOrigin: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:forms.origin.${key}`);

  const tRoute: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:forms.route.${key}`);

  const tSecond = () => `${Translation.Common}:tables.seconds`;

  const DOMAIN_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.CdnDomain,
      endpoint: ApiEndpoint.DEFAULT,
      formActionType: isEdit ? FormActionType.Edit : FormActionType.Create,
      modalName: ServiceModalName.FormModal,
      label: {
        title: tModal(`${isEdit ? 'editDomain' : 'addNewDomain'}`),
        description: tModal(`${isEdit ? 'editDomainDescription' : 'addNewDomainDescription'}`),
        confirmButton: tButton(`${isEdit ? 'editDomain' : 'addDomain'}`),
      },
      inputList: [
        {
          name: 'name',
          defaultValue: '',
          inputType: InputType.Text,
          required: true,
          placeholder: tForm('enterYourName'),
          label: tForm('generalName'),
          direction: columnDirection.Row,
        },
        {
          name: 'domain',
          defaultValue: '',
          inputType: InputType.Text,
          required: true,
          placeholder: tForm('enterYourDomain'),
          label: tForm('domainName'),
          direction: columnDirection.Row,
          hints: tValidation('domain'),
        },
        {
          name: 'type',
          defaultValue: originType.HTTPS,
          inputType: InputType.Select,
          placeholder: tOrigin('selectYourOriginType'),
          label: tOrigin('originType'),
          direction: columnDirection.Row,
          options: originOptionList,
          collapsible: true,
          collapsibleTitle: tForm('advanceOptions'),
          collapsibleGroup: 'advanced_options',
        },
        {
          name: 'origin',
          defaultValue: '',
          inputType: InputType.Text,
          placeholder: tOrigin('enterYourIpv4Origin'),
          label: tForm('origin'),
          direction: columnDirection.Row,
          hints: tValidation('ip'),
          collapsible: true,
          collapsibleGroup: 'advanced_options',
        },
        {
          name: 'connect_timeout',
          defaultValue: TIMEOUT_LIMITS.DEFAULT_TIMEOUT,
          type: 'number' as const,
          placeholder: '0',
          label: tRoute('connectionTimeOut'),
          direction: columnDirection.Row,
          min: TIMEOUT_LIMITS.MIN_CONNECTION_SEND,
          max: TIMEOUT_LIMITS.MAX_CONNECTION_SEND,
          adornment: TimeoutAdornment(tSecond()),
          adornmentClassName: 'pr-3',
          collapsible: true,
          collapsibleGroup: 'advanced_options',
        },
        {
          name: 'send_timeout',
          defaultValue: TIMEOUT_LIMITS.DEFAULT_TIMEOUT,
          type: 'number' as const,
          placeholder: '0',
          label: tRoute('sendTimeOut'),
          direction: columnDirection.Row,
          min: TIMEOUT_LIMITS.MIN_CONNECTION_SEND,
          max: TIMEOUT_LIMITS.MAX_CONNECTION_SEND,
          adornment: TimeoutAdornment(tSecond()),
          adornmentClassName: 'pr-3',
          collapsible: true,
          collapsibleGroup: 'advanced_options',
        },
        {
          name: 'read_timeout',
          defaultValue: TIMEOUT_LIMITS.DEFAULT_TIMEOUT,
          type: 'number' as const,
          placeholder: '0',
          label: tRoute('readTimeOut'),
          direction: columnDirection.Row,
          min: TIMEOUT_LIMITS.MIN_READ,
          max: TIMEOUT_LIMITS.MAX_READ,
          adornment: TimeoutAdornment(tSecond()),
          adornmentClassName: 'pr-3',
          collapsible: true,
          collapsibleGroup: 'advanced_options',
        },
        {
          name: 'verify_ssl_cert',
          defaultValue: true,
          inputType: InputType.Switch,
          label: tOrigin('verifySSL'),
          direction: columnDirection.Row,
          collapsible: true,
          collapsibleGroup: 'advanced_options',
          hide: {
            field: 'type',
            condition: (value: string) => value !== originType.HTTPS,
          },
        },
      ],
      validationSchema: schema,
    },
  };

  return (DOMAIN_FORM?.[endpoint] ?? DOMAIN_FORM[ApiEndpoint.DEFAULT]) as FormConfig;
};
